/**
 * SummarySection Component
 * Detail-page header: title, tags pill, docs, and reference links (Concept V3).
 */

import React, { useRef, useCallback, useState } from "react";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Tooltip from "@mui/material/Tooltip";
import { FieldRenderer } from "./FieldRenderer";
import { TagsCountIndicator } from "./TagsCountIndicator";
import { resolveDisplayTagGroups } from "../../../services/regulationAutoTags";
import { pickRegulationCategoriesValue } from "../../../services/regulationsService";
import { RegulationFavoriteButton } from "../RegulationFavoriteButton";
import { FAVORITE_ICON_SLOT_WIDTH_PX } from "../../RegulationsTable/regulationFavoriteIcons";
import type { FieldConfig, AssessmentDocument } from "../../../types";

interface SummarySectionProps {
  loading: boolean;
  regulationData: Record<string, unknown> | null;
  fields: FieldConfig[];
  /**
   * List-page fields config. Kept for call-site compatibility; upper summary
   * no longer renders dynamic list TAG columns (Concept V3).
   */
  listFields?: FieldConfig[];
  onDocumentClick?: (doc: AssessmentDocument) => void;
  onDocumentDownload?: (doc: AssessmentDocument) => void;
  title?: string;
  actionButton?: React.ReactNode;
  /** When set, shows favorite control after the title */
  favoriteRegulationId?: string | null;
  isFavorite?: boolean;
  onFavoriteChange?: (isFavorite: boolean) => void;
  /** Backend message when the detail fetch fails */
  fetchErrorMessage?: string | null;
}

export const SummarySection: React.FC<SummarySectionProps> = ({
  loading,
  regulationData,
  fields,
  onDocumentClick,
  onDocumentDownload,
  title,
  actionButton,
  favoriteRegulationId,
  isFavorite = false,
  onFavoriteChange,
  fetchErrorMessage,
}) => {
  const titleRef = useRef<HTMLElement>(null);
  const [isTitleTruncated, setIsTitleTruncated] = useState(false);

  const handleTitleMouseEnter = useCallback(() => {
    const el = titleRef.current;
    if (el) setIsTitleTruncated(el.scrollHeight > el.clientHeight);
  }, []);

  if (loading) {
    return (
      <Box
        sx={{
          borderRadius: "4px",
          bgcolor: "background.paper",
          p: 2,
          mb: 2,
        }}
      >
        <Typography variant="body2" sx={{ color: "#6b7280" }}>
          Loading regulation details...
        </Typography>
      </Box>
    );
  }

  if (!regulationData) {
    return (
      <Box
        sx={{
          borderRadius: "4px",
          bgcolor: "background.paper",
          p: 2,
          mb: 2,
        }}
      >
        <Typography variant="body2" color="error">
          {fetchErrorMessage || "Failed to load regulation details"}
        </Typography>
      </Box>
    );
  }

  const fieldMap = new Map<string, FieldConfig>();
  fields.forEach((field) => {
    fieldMap.set(field.field_name, field);
  });

  const getField = (name: string) => fieldMap.get(name);
  const tagGroups = resolveDisplayTagGroups(regulationData);
  const rawCategories = pickRegulationCategoriesValue(regulationData);
  const categoryItems = Array.isArray(rawCategories) ? rawCategories : [];
  const regulationId =
    typeof regulationData.regulation_id === "string"
      ? regulationData.regulation_id
      : typeof regulationData.id === "string"
        ? regulationData.id
        : null;

  const titleText = String(
    title || regulationData.title || regulationData.regulation_id || "",
  );

  return (
    <Box
      sx={{
        borderRadius: "4px",
        bgcolor: "background.paper",
        p: 2,
        mb: 2,
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "flex-start",
          justifyContent: "space-between",
          gap: 2,
          mb: "20px",
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "flex-start",
            flexWrap: "wrap",
            gap: 0.5,
            rowGap: 1,
            flex: 1,
            minWidth: 0,
          }}
        >
          <Tooltip
            title={isTitleTruncated ? titleText : ""}
            arrow
            placement="bottom-start"
            slotProps={{
              tooltip: {
                sx: {
                  maxWidth: 560,
                  fontSize: (theme) => theme.typography.body2.fontSize,
                  lineHeight: 1.5,
                  py: 1,
                  px: 1.5,
                },
              },
            }}
          >
            <Typography
              ref={titleRef}
              component="h1"
              onMouseEnter={handleTitleMouseEnter}
              sx={{
                fontWeight: 700,
                fontSize: "20px",
                color: "text.primary",
                lineHeight: 1.5,
                minWidth: 0,
                flex: "0 1 auto",
                maxWidth: {
                  xs: "100%",
                  md: favoriteRegulationId && onFavoriteChange
                    ? `calc(560px - ${FAVORITE_ICON_SLOT_WIDTH_PX}px)`
                    : "560px",
                },
                display: "-webkit-box",
                WebkitLineClamp: 2,
                WebkitBoxOrient: "vertical",
                overflow: "hidden",
                textOverflow: "ellipsis",
              }}
            >
              {titleText}
            </Typography>
          </Tooltip>

          <TagsCountIndicator
            groups={tagGroups}
            categoryItems={categoryItems}
            regulationId={regulationId}
            regulationTitle={titleText}
            testId="summary-tags-pill"
          />

          {favoriteRegulationId && onFavoriteChange ? (
            <Box sx={{ alignSelf: "center", ml: 0.5 }}>
              <RegulationFavoriteButton
                regulationId={favoriteRegulationId}
                isFavorite={isFavorite}
                onFavoriteChange={onFavoriteChange}
                disabled={loading}
              />
            </Box>
          ) : null}
        </Box>
        {actionButton ? (
          <Box sx={{ flexShrink: 0 }}>{actionButton}</Box>
        ) : null}
      </Box>

      {getField("associated_documents") ? (
        <Box
          sx={{
            mb: "18px",
            "& .MuiTypography-caption": {
              color: "#364153",
              fontSize: "15px",
              fontWeight: 400,
              mb: 1.5,
            },
            "& .MuiBox-root > .MuiBox-root": {
              mt: 0,
              gap: 2,
            },
          }}
        >
          <FieldRenderer
            field={getField("associated_documents")!}
            value={regulationData.associated_documents}
            onDocumentClick={onDocumentClick}
            onDocumentDownload={onDocumentDownload}
            regulationId={regulationData.regulation_id as string}
          />
        </Box>
      ) : null}

      {getField("reference_links") ? (
        <Box
          sx={{
            mb: 0,
            "& .MuiTypography-caption": {
              color: "#6A7282",
              fontSize: "12px",
              fontWeight: 400,
              mb: 1,
            },
          }}
        >
          <FieldRenderer
            field={getField("reference_links")!}
            value={regulationData.reference_links}
          />
        </Box>
      ) : null}
    </Box>
  );
};
